import { CaretLeftIcon, CaretRightIcon, SidebarSimpleIcon } from "@phosphor-icons/react";
import React, { useEffect, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { NAV_SECTIONS, isItemActive, isSectionActive } from "./navigation";
import "./Sidebar.scss";

const COLLAPSED_KEY = "blikst.sidebarCollapsed";

const readCollapsed = () => {
  try {
    return localStorage.getItem(COLLAPSED_KEY) === "1";
  } catch {
    return false;
  }
};

const activeCollapsibleLabels = (pathname) => NAV_SECTIONS.filter((s) => s.collapsible && isSectionActive(pathname, s)).map((s) => s.label);

const Sidebar = () => {
  const { pathname } = useLocation();
  const [collapsed, setCollapsed] = useState(readCollapsed);
  const [openSections, setOpenSections] = useState(() => new Set(activeCollapsibleLabels(pathname)));

  useEffect(() => {
    try {
      localStorage.setItem(COLLAPSED_KEY, collapsed ? "1" : "0");
    } catch {
      // Storage unavailable: the toggle still works for this session.
    }
  }, [collapsed]);

  // Landing on a page inside a closed section opens that section.
  useEffect(() => {
    const labels = activeCollapsibleLabels(pathname);
    if (labels.length === 0) return;
    setOpenSections((prev) => {
      if (labels.every((l) => prev.has(l))) return prev;
      const next = new Set(prev);
      labels.forEach((l) => next.add(l));
      return next;
    });
  }, [pathname]);

  const toggleSection = (label) =>
    setOpenSections((prev) => {
      const next = new Set(prev);
      if (next.has(label)) next.delete(label);
      else next.add(label);
      return next;
    });

  const renderItem = ({ to, label, icon: Icon }, nested = false) => (
    <NavLink
      key={to}
      to={to}
      end
      title={collapsed ? label : undefined}
      className={() => `sidebar-link${nested ? " nested" : ""}${isItemActive(pathname, to) ? " active" : ""}`}
    >
      <Icon size={18} />
      <span className="sidebar-link-label">{label}</span>
    </NavLink>
  );

  const renderCollapsibleSection = (section) => {
    const { label, icon: Icon, to, items } = section;
    const active = isSectionActive(pathname, section);
    const open = openSections.has(label);

    if (collapsed) {
      return (
        <NavLink
          to={to}
          title={label}
          className={() => `sidebar-link${active ? " active" : ""}`}
        >
          <Icon size={18} />
          <span className="sidebar-link-label">{label}</span>
        </NavLink>
      );
    }

    return (
      <>
        <button
          type="button"
          className={`sidebar-link sidebar-group-toggle${active && !open ? " active" : ""}`}
          onClick={() => toggleSection(label)}
          aria-expanded={open}
        >
          <Icon size={18} />
          <span className="sidebar-link-label">{label}</span>
          <CaretRightIcon
            size={13}
            className={`sidebar-group-caret${open ? " open" : ""}`}
          />
        </button>
        {open && <div className="sidebar-group-items">{items.map((item) => renderItem(item, true))}</div>}
      </>
    );
  };

  return (
    <aside className={`sidebar${collapsed ? " collapsed" : ""}`}>
      <div className="sidebar-collapse-row">
        <button
          type="button"
          className="sidebar-collapse"
          onClick={() => setCollapsed((v) => !v)}
          aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
          title={collapsed ? "Expand sidebar" : "Collapse sidebar"}
        >
          {collapsed ? <CaretRightIcon size={13} /> : <CaretLeftIcon size={13} />}
          <SidebarSimpleIcon size={16} />
        </button>
      </div>

      <nav className="sidebar-nav">
        {NAV_SECTIONS.map((section) => (
          <div
            key={section.label}
            className="sidebar-card"
          >
            {section.collapsible ? renderCollapsibleSection(section) : section.items.map((item) => renderItem(item))}
          </div>
        ))}
      </nav>
    </aside>
  );
};

export default Sidebar;
